import {
  add,
  differenceInCalendarDays,
  differenceInMilliseconds,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  getDay,
  getDate,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  isSameMonth,
  isSameYear,
  millisecondsToSeconds,
  parse,
  secondsInDay,
  secondsInHour,
  startOfDay,
  startOfMonth,
  startOfWeek,
  sub,
  isValid,
  parseISO,
} from 'date-fns';

import type { DateFormat, PrecisionTypes } from './definitions';

export class SemboDate {
  public date: Date = new Date();

  constructor(date?: string | Date | null, format?: DateFormat) {
    if (typeof date === 'string') {
      this.date = format ? parse(date, format, new Date()) : parseISO(date);
      if (isNaN(this.date.getTime())) {
        throw new Error(
          `Invalid date with props: ${JSON.stringify({ date, format })}`
        );
      }
    }

    if (date instanceof Date) {
      this.date = date;
    }
  }

  // consider rename to toDate
  public get(): Date {
    return this.date;
  }

  public set(newDate: Date): void {
    this.date = newDate;
  }

  public getDay(): number {
    return getDay(this.date);
  }

  public getDaysInMonth(): number {
    return getDate(this.date);
  }

  // Return this to chain functions e.g. with format
  public add(duration: Duration): SemboDate {
    this.date = add(this.date, duration);
    return this;
  }

  // Return this to chain functions e.g. with format
  public sub(duration: Duration): SemboDate {
    this.date = sub(this.date, duration);
    return this;
  }

  public format(dateFormat: DateFormat): string {
    return format(this.date, dateFormat);
  }

  public formatUTC(dateFormat: DateFormat): string {
    return format(this.timeZonedToUTC(this.date), dateFormat);
  }

  // FYI: default in sec
  public diff(dateToDiff: Date, precision?: PrecisionTypes): number {
    const result = differenceInMilliseconds(this.date, dateToDiff);
    const resultlInSeconds = millisecondsToSeconds(result);

    switch (precision) {
      case 'calendarDay':
        return differenceInCalendarDays(this.date, dateToDiff);

      case 'day':
        return Math.floor(resultlInSeconds / secondsInDay);

      case 'hour':
        return Math.floor(resultlInSeconds / secondsInHour);

      case 'second':
        return resultlInSeconds;

      default:
        return result;
    }
  }

  // FYI: returns new SemboDate with start given precision
  public startOf(precision: PrecisionTypes): SemboDate {
    let newDate: string | undefined;
    switch (precision) {
      case 'month': {
        newDate = startOfMonth(this.date).toISOString();
        break;
      }
      case 'week': {
        newDate = startOfWeek(this.date).toISOString();
        break;
      }
      case 'day': {
        newDate = startOfDay(this.date).toISOString();
        break;
      }
    }
    return new SemboDate(newDate);
  }

  // FYI: returns new SemboDate with start given precision
  public endOf(precision: PrecisionTypes): SemboDate {
    let newDate: string | undefined;
    switch (precision) {
      case 'month': {
        newDate = endOfMonth(this.date).toISOString();
        break;
      }
      case 'week': {
        newDate = endOfWeek(this.date).toISOString();
        break;
      }
      case 'day': {
        newDate = endOfDay(this.date).toISOString();
        break;
      }
    }
    return new SemboDate(newDate);
  }

  // TODO: add precision of comparision
  public isSame(dateToCompare: Date): boolean {
    return isEqual(this.date, dateToCompare);
  }

  public isBetween(date1: Date, date2: Date): boolean {
    return this.isAfter(date1) && this.isBefore(date2);
  }

  public isAfter(dateToCompare: Date): boolean {
    return isAfter(this.date, dateToCompare);
  }

  public isBefore(dateToComapre: Date): boolean {
    return isBefore(this.date, dateToComapre);
  }

  public isSameDay(date: Date): boolean {
    return isSameDay(this.date, date);
  }

  public isSameMonth(date: Date): boolean {
    return isSameMonth(this.date, date);
  }

  public isSameYear(date: Date): boolean {
    return isSameYear(this.date, date);
  }

  public clone(): SemboDate {
    return new SemboDate(this.date.toISOString());
  }

  public isValidDate(): boolean {
    return isValid(this.date);
  }

  private timeZonedToUTC(date: Date): Date {
    return new SemboDate(date).add({ minutes: date.getTimezoneOffset() }).get();
  }
}
